import React, { useCallback, useEffect, useState } from 'react';
import { Button, ConfigProvider, Form, Input, Modal, Select, Space, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useAxiosClient from "../axios-client.js";
import { useStateContext } from "../contexts/ContextProvider.jsx";
import { shellAntdTheme } from "../shellTokens";
import RolePermissions from "../components/Settings/RoleSettings/RolePermissions.jsx";
import AssignUserRoles from "../components/Settings/RoleSettings/AssignUserRoles.jsx";
import "../components/Settings/RoleSettings/access.css";

/**
 * Settings → Access, Roles tab.
 *
 * The role list is informative before you click it — permission and user counts
 * per role — and the pane beside it answers "what can this role do".
 *
 * Below 1100px the list becomes a Select; below 768px the screen goes
 * read-only. Editing needs the full permission list and a save bar in view at
 * once, and granting rights on a phone is a mis-tap with no undo behind it.
 */

const EDIT_MIN_WIDTH = 768;

const useReadOnlyViewport = () => {
  const [readOnly, setReadOnly] = useState(() => window.innerWidth < EDIT_MIN_WIDTH);
  useEffect(() => {
    const onResize = () => setReadOnly(window.innerWidth < EDIT_MIN_WIDTH);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return readOnly;
};

const RoleSettings = () => {
  const axiosClient = useAxiosClient();
  const navigate = useNavigate();
  const { darkMode, permissions } = useStateContext();
  const [form] = Form.useForm();
  const [modal, contextHolder] = Modal.useModal();

  const [roles, setRoles] = useState([]);
  const [role, setRole] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tab, setTab] = useState('permissions');
  const [pending, setPending] = useState({ dirty: false, added: 0, removed: 0, pendingTotal: 0 });

  const readOnly = useReadOnlyViewport();
  const canSeePermissions = (permissions || []).includes('PERMISSION_SETTINGS');

  const getRoles = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axiosClient.get(`/settings/getRoles`);
      setRoles(response.data);
      setRole((current) => current ?? response.data[0]?.name ?? null);
    } catch (error) {
      const message = (error.response && error.response.data && error.response.data.message)
        || error.message || error.toString();
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  }, [axiosClient]);

  useEffect(() => { getRoles(); }, [getRoles]);

  /* ---- Unsaved-change guards ------------------------------------------- */
  useEffect(() => {
    if (!pending.dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [pending.dirty]);

  const guarded = (action) => {
    if (!pending.dirty) return action();
    return modal.confirm({
      title: 'Discard unsaved permission changes?',
      content: `${pending.added} added and ${pending.removed} removed have not been saved.`,
      okText: 'Discard',
      okButtonProps: { danger: true },
      cancelText: 'Keep editing',
      onOk: action,
    });
  };

  const selectRole = (name) => {
    if (name === role) return;
    guarded(() => {
      setPending({ dirty: false, added: 0, removed: 0, pendingTotal: 0 });
      setRole(name);
      setTab('permissions');
    });
  };

  const onFinish = async (data) => {
    setCreating(true);
    try {
      const response = await axiosClient.post(`/settings/createRole`, data);
      toast.success(response.data.message);
      setIsModalOpen(false);
      form.resetFields();
      await getRoles();
      setRole(data.name);
    } catch (error) {
      const message = (error.response && error.response.data && error.response.data.message)
        || error.message || error.toString();
      toast.error(message);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteRole = (target) => {
    modal.confirm({
      title: `Delete the ${target.name} role?`,
      content: target.usersCount > 0
        ? `${target.usersCount} user${target.usersCount === 1 ? '' : 's'} will lose the ${target.permissionsCount} permissions it grants.`
        : 'It grants no access to anyone right now.',
      okText: 'Delete role',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const response = await axiosClient.delete(`/roles/${target.id}`);
          toast.success(response.data.message);
          setRole(null);
          getRoles();
        } catch (error) {
          const message = (error.response && error.response.data && error.response.data.message)
            || error.message || error.toString();
          toast.error(message);
        }
      },
    });
  };

  const selected = roles.find((r) => r.name === role);
  // While unsaved, the count shown is where the role is heading, not where it is.
  const shownCount = pending.dirty ? pending.pendingTotal : selected?.permissionsCount ?? 0;

  const roleMeta = (r) => `${r.permissionsCount} permission${r.permissionsCount === 1 ? '' : 's'} · ${r.usersCount} user${r.usersCount === 1 ? '' : 's'}`;

  return (
    <ConfigProvider theme={shellAntdTheme(darkMode)}>
      <div className="et-access animate-fade-in">
        <div style={{ marginBottom: 14 }}>
          <div style={{ font: '400 12px Outfit, sans-serif', color: 'var(--dim)' }}>Settings</div>
          <h1 style={{ font: '700 22px Outfit, sans-serif', letterSpacing: '-.01em', margin: '1px 0 0' }}>
            Access
          </h1>
        </div>

        <div className="et-access__tabs" style={{ marginBottom: 16 }}>
          <button type="button" className="et-access__tab et-access__tab--on">Roles</button>
          {canSeePermissions && (
            <button
              type="button"
              className="et-access__tab"
              onClick={() => guarded(() => navigate('/settings/permissionSettings'))}
            >
              Permissions
            </button>
          )}
        </div>

        {readOnly && (
          <div style={{
            marginBottom: 12,
            padding: '10px 12px',
            borderRadius: 10,
            background: 'var(--surface2)',
            border: '1px solid var(--border)',
            font: '400 12px/1.5 Outfit, sans-serif',
            color: 'var(--muted)',
          }}>
            Roles are read-only here. Editing permissions needs the full list and a save bar,
            which do not fit a phone.
          </div>
        )}

        <div className="et-access__cols">
          {/* Role picker: a list on desktop, a Select once the column goes. */}
          <div className="et-access__rolepicker" style={{ marginBottom: 4 }}>
            <Space.Compact style={{ width: '100%' }}>
              <Select
                value={role}
                onChange={selectRole}
                loading={isLoading}
                style={{ width: '100%' }}
                options={roles.map((r) => ({ value: r.name, label: `${r.name} — ${roleMeta(r)}` }))}
              />
              {!readOnly && <Button onClick={() => setIsModalOpen(true)}>+ New</Button>}
            </Space.Compact>
          </div>

          <div className="et-access__rolelist et-access__card">
            <div className="et-access__cardhead">
              <div>
                <div style={{ font: '700 14.5px Outfit, sans-serif' }}>Roles</div>
                <div style={{ font: '400 12px Outfit, sans-serif', color: 'var(--muted)', marginTop: 2 }}>
                  {`${roles.length} role${roles.length === 1 ? '' : 's'}`}
                </div>
              </div>
              {!readOnly && <Button size="small" onClick={() => setIsModalOpen(true)}>+ New</Button>}
            </div>
            <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {isLoading && !roles.length && <div style={{ padding: 20, textAlign: 'center' }}><Spin /></div>}
              {roles.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  className={`et-access__role${r.name === role ? ' et-access__role--on' : ''}`}
                  onClick={() => selectRole(r.name)}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <span style={{
                      font: `${r.name === role ? 700 : 600} 13px Outfit, sans-serif`,
                      color: r.name === role ? 'var(--activeText)' : 'var(--text)',
                    }}>
                      {r.name}
                    </span>
                    {r.protected && <span className="et-access__chip et-access__chip--protected">PROTECTED</span>}
                    {r.name === role && pending.dirty && (
                      <span style={{ width: 6, height: 6, borderRadius: 3, background: 'var(--primary)' }} />
                    )}
                  </span>
                  <span className="et-access__meta" style={{
                    color: r.name === role ? 'var(--activeText)' : 'var(--dim)',
                    opacity: r.name === role ? 0.72 : 1,
                  }}>
                    {r.name === role && pending.dirty
                      ? `${r.permissionsCount} → ${pending.pendingTotal} permissions · ${r.usersCount} users`
                      : roleMeta(r)}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="et-access__card" style={{ display: 'flex', flexDirection: 'column' }}>
            {!role ? (
              <div style={{ padding: 60, textAlign: 'center', color: 'var(--dim)' }}>
                Select a role to view its access.
              </div>
            ) : (
              <>
                <div className="et-access__cardhead">
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ font: '700 14.5px Outfit, sans-serif' }}>{role}</span>
                      {selected?.protected && (
                        <span className="et-access__chip et-access__chip--protected">PROTECTED</span>
                      )}
                    </div>
                    <div style={{ font: '400 12px Outfit, sans-serif', color: 'var(--muted)', marginTop: 2 }}>
                      <span className="et-access__meta">{`${shownCount} of ${selected?.permissionsCount ?? 0}`}</span>
                      {' permissions · '}
                      <span className="et-access__meta">{selected?.usersCount ?? 0}</span>
                      {` user${(selected?.usersCount ?? 0) === 1 ? '' : 's'}`}
                      {selected?.protected && ' · this role cannot be deleted'}
                    </div>
                  </div>
                  {!readOnly && selected && !selected.protected && (
                    <Button danger onClick={() => handleDeleteRole(selected)}>Delete role</Button>
                  )}
                </div>

                <div className="et-access__tabs" style={{ padding: '0 18px' }}>
                  <button
                    type="button"
                    className={`et-access__tab${tab === 'permissions' ? ' et-access__tab--on' : ''}`}
                    onClick={() => setTab('permissions')}
                  >
                    Permissions
                    <span className="et-access__tabcount">{shownCount}</span>
                  </button>
                  <button
                    type="button"
                    className={`et-access__tab${tab === 'users' ? ' et-access__tab--on' : ''}`}
                    onClick={() => guarded(() => setTab('users'))}
                  >
                    Assigned users
                    <span className="et-access__tabcount">{selected?.usersCount ?? 0}</span>
                  </button>
                </div>

                {tab === 'permissions' ? (
                  <RolePermissions
                    key={role}
                    role={role}
                    readOnly={readOnly}
                    onDirtyChange={setPending}
                    onSaved={() => { setPending({ dirty: false, added: 0, removed: 0, pendingTotal: 0 }); getRoles(); }}
                  />
                ) : (
                  <AssignUserRoles key={role} role={role} onMembershipChange={getRoles} />
                )}
              </>
            )}
          </div>
        </div>

        <Modal
          title="Add a new role"
          open={isModalOpen}
          onCancel={() => { form.resetFields(); setIsModalOpen(false); }}
          footer={null}
        >
          <Form form={form} layout="vertical" onFinish={onFinish} style={{ marginTop: 16 }}>
            <Form.Item
              name="name"
              label="Role name"
              rules={[{ required: true, message: 'Please input role name!' }]}
            >
              <Input placeholder="e.g. Packer" />
            </Form.Item>
            <div style={{ font: '400 12px/1.5 Outfit, sans-serif', color: 'var(--muted)', marginBottom: 14 }}>
              A new role starts with no permissions. You will be taken to it to grant them.
            </div>
            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={() => { form.resetFields(); setIsModalOpen(false); }}>Cancel</Button>
                <Button type="primary" htmlType="submit" loading={creating}>Create role</Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
        {contextHolder}
      </div>
    </ConfigProvider>
  );
};

export default RoleSettings;
